// Single source of truth for staff-category permissions (Phase 1 of the staff-roles work).
// Every role decision in the app goes through here — never compare role strings inline.
//
// Design rule: DENY BY DEFAULT. An unknown, missing or legacy role value gets no
// privileges anywhere. The legacy value "staff" intentionally no longer exists: after the
// one-time migration (scripts/migrate-staff-roles.cjs) any check that still looks for it
// matches nobody, so a missed call-site fails closed instead of silently granting
// compliance powers. See docs/staff-roles-permission-map.md for the frozen field map.

export const ROLES = Object.freeze({
  ADMIN: "admin",
  MANAGER: "manager",
  STAFF_COMPLIANCE: "staff_compliance",
  STAFF_FRONT: "staff_front",
});

export const KNOWN_ROLES = Object.freeze([
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.STAFF_COMPLIANCE,
  ROLES.STAFF_FRONT,
]);

export const ROLE_LABELS = Object.freeze({
  [ROLES.ADMIN]: "Admin",
  [ROLES.MANAGER]: "Manager",
  [ROLES.STAFF_COMPLIANCE]: "Staff compliance",
  [ROLES.STAFF_FRONT]: "Staff front",
});

/** Normalise a stored role value. Anything unrecognised becomes "" (no privileges). */
export function normalizeRole(value) {
  const role = String(value || "").trim().toLowerCase();
  return KNOWN_ROLES.includes(role) ? role : "";
}

export const roleLabel = (value) => ROLE_LABELS[normalizeRole(value)] || "Unknown role";

export const isKnownRole = (value) => KNOWN_ROLES.includes(normalizeRole(value));

/** Either staff category — where the old code asked `role === "staff"` about scope. */
export const isStaffCategory = (value) => {
  const role = normalizeRole(value);
  return role === ROLES.STAFF_COMPLIANCE || role === ROLES.STAFF_FRONT;
};

// ---------------------------------------------------------------------------
// Protected areas (read-only for Staff front). Names match the frozen map.
// ---------------------------------------------------------------------------
export const PROTECTED_AREAS = Object.freeze({
  RISK_ASSESSMENT: "riskAssessment",
  SCREENING: "screening",
  DOCUMENTS_CHECKLIST: "documentsChecklist",
  FINAL_OVERVIEW: "finalOverview",
  DOCUMENTS_REVIEW: "documentsReview",
});

const PROTECTED_AREA_VALUES = Object.freeze(Object.values(PROTECTED_AREAS));

/**
 * May this role change protected compliance information?
 * Staff front: never. Compliance/manager: yes. Admin keeps its existing app-level
 * behaviour (admin pages), not extra KYC powers — but is not the concern of this gate.
 * Unknown role or unknown area: never.
 */
export function canEditProtectedArea(role, area) {
  if (!PROTECTED_AREA_VALUES.includes(area)) return false;
  const r = normalizeRole(role);
  return r === ROLES.STAFF_COMPLIANCE || r === ROLES.MANAGER || r === ROLES.ADMIN;
}

export const isReadOnlyArea = (role, area) => !canEditProtectedArea(role, area);

/** Ordinary KYC information (customer, ownership, SOW declarations, …). */
export function canEditOrdinaryKyc(role) {
  return isKnownRole(role);
}

/** Client deletion destroys protected decisions — front office may not. */
export function canDeleteClient(role) {
  const r = normalizeRole(role);
  return r === ROLES.STAFF_COMPLIANCE || r === ROLES.MANAGER || r === ROLES.ADMIN;
}

/** Transient (non-persistent) screening lookups are allowed for both staff categories. */
export function canRunTransientCheck(role) {
  return isKnownRole(role);
}

/** Persisting screening results / decisions is a protected act. */
export const canPersistScreening = (role) =>
  canEditProtectedArea(role, PROTECTED_AREAS.SCREENING);

/** Approving / rejecting documents and saving review decisions. */
export const canDecideDocumentReview = (role) =>
  canEditProtectedArea(role, PROTECTED_AREAS.DOCUMENTS_REVIEW);

/** Sending client invites (e-form / doc-upload links) is front-office work: both may. */
export function canSendClientInvites(role) {
  return isKnownRole(role);
}

// ---------------------------------------------------------------------------
// KYC document subtrees — shared vocabulary for the masked save (Phase 4) and
// the rules tests (Phase 5). Keys are the top-level fields of kyc/{clientId}.
// ---------------------------------------------------------------------------
export const PROTECTED_KYC_SUBTREES = Object.freeze([
  "internalUse",
  "sanctions",
  "pep",
  "adverseMedia",
  "pepSanctions",
  "documentsChecklist",
  "finalOverview",
]);

export const PERMITTED_KYC_SUBTREES = Object.freeze([
  "customer",
  "ownership",
  "sourceOfFundsWealth",
  "uaeCompany",
  "engagement",
]);

// Root-level flags that carry outcome semantics; Staff front saves must not set them.
export const PROTECTED_KYC_ROOT_FIELDS = Object.freeze(["clientStatus", "kycCompleted"]);
